/**
 * Human-readable descriptions for predefined event and user properties
 * shown in the property filter picker detail pane. Custom properties use name-only UI.
 */

const EVENT_PROPERTY_DESCRIPTIONS = {
  distinct_id:
    "Anonymous or known device identifier used to stitch events before identity resolution. Distinct IDs group activity per browser or device until the user is identified.",
  user_identity:
    "Whether the event was sent for an identified user (logged in or aliased) versus an anonymous distinct ID only.",
  country: "Country inferred from IP or client context at the time of the event.",
  city: "City inferred from IP or client context at the time of the event.",
  region: "Region or state inferred from IP or client context.",
  timezone: "Time zone reported by the client when the event was captured.",
  browser_name: "Browser name (e.g. Chrome, Safari) from the user agent.",
  browser_version: "Browser version string from the user agent.",
  os: "Operating system family (e.g. Windows, macOS, iOS, Android).",
  os_version: "Operating system version from the user agent.",
  device_type: "Coarse device class such as desktop, mobile, or tablet.",
  language: "Preferred language (locale) reported by the browser.",
  current_url: "Full page URL at the moment the event was recorded.",
  page_title: "Document title of the page when the event fired.",
  uri: "Path and query portion of the URL (without scheme/host).",
  initial_referrer: "Referrer URL from the first page in the session.",
  referrer: "Referrer URL for the navigation that led to this page or event.",
  utm_source: "UTM source tag from the landing URL (campaign attribution).",
  utm_medium: "UTM medium tag (e.g. email, cpc) from the landing URL.",
  utm_campaign: "UTM campaign name from the landing URL.",
  utm_term: "UTM term (often paid search keyword) from the landing URL.",
  utm_content: "UTM content variant for A/B or creative-level attribution.",
  utm_id: "UTM campaign id when provided in the landing URL.",
  element_name: "Name attribute of the clicked or targeted DOM element (auto events).",
  element_id: "HTML id of the clicked or targeted element.",
  element_class: "CSS class list of the clicked or targeted element.",
  element_tag_name: "HTML tag name of the element (e.g. button, a).",
  element_type: "Input type or control kind when applicable.",
  element_text: "Visible text content associated with the element (truncated when long).",
  viewport_height: "Inner height of the browser viewport in pixels.",
  viewport_width: "Inner width of the browser viewport in pixels.",
  screen_height: "Physical screen height reported by the device.",
  screen_width: "Physical screen width reported by the device.",
  dpi: "Device pixel ratio or density hint when available.",
  is_web3_user: "Whether the client marked this session or user as Web3-capable.",
  wallet_address: "Whether a wallet address was present on the event payload (existence-style filter).",
};

/** Global / session filter user props (user.* names) */
const USER_PROPERTY_DESCRIPTIONS = {
  "user.is_identified": "Profile is linked to a known user (not only an anonymous device).",
  "user.is_bounce": "Whether the user’s session qualified as a bounce under your product rules.",
  "user.is_web3_user": "User-level flag for Web3 engagement across sessions.",
  "user.is_wallet_connected": "Whether a wallet connection was recorded for this user.",
  "user.transacted_user": "Whether the user has completed a tracked transaction.",
  "user.first_visit": "Timestamp of the user’s first recorded visit.",
  "user.last_visit": "Timestamp of the user’s most recent visit.",
  "user.total_page_views": "Cumulative page views attributed to this user profile.",
  "user.total_time_spent": "Cumulative active or session time attributed to this user.",
  "user.first_referrer": "Referrer on the user’s first landing page.",
  "user.last_referrer": "Referrer on the user’s most recent landing or navigation.",
  "user.first_location_city": "City associated with the user’s earliest known location.",
  "user.first_location_country": "Country associated with the user’s earliest known location.",
  "user.last_location_city": "City from the user’s latest known location signal.",
  "user.last_location_country": "Country from the user’s latest known location signal.",
  "user.device_type": "Primary device class observed for this user.",
  "user.browser_name": "Most common or latest browser name for this user.",
  "user.os": "Operating system family associated with this user profile.",
};

/**
 * Cohort / Users list filters — field names match getFixedUserProperties / userIdentityFilterBuilder.
 */
const USER_IDENTITY_FIXED_DESCRIPTIONS = {
  userId:
    "The tracked user identifier (distinct id) stored on the user profile. Use this to target or exclude specific users.",
  isIdentified:
    "Whether the profile is linked to a known identity (logged in, aliased, or otherwise identified) versus anonymous device-only activity.",
  hasWalletAddress:
    "Whether the user has a non-empty primary wallet address on their profile (Web3 linkage).",
  isBounceUser:
    "Whether the user’s behavior met your product’s bounce definition (e.g. very short or single-page engagement).",
  isWeb3User:
    "User-level flag indicating Web3-related engagement across their sessions and profile.",
  firstVisit:
    "Timestamp of the first visit Trodo recorded for this user. Useful for signup cohorts and age-of-user filters.",
  lastVisit:
    "Timestamp of the most recent visit. Use for recency, churn risk, and active-user segments.",
  totalPageViews:
    "Lifetime count of page views attributed to this user across tracked sessions.",
  totalTimeSpent:
    "Cumulative engaged or session time attributed to this user (product-defined).",
  totalSessions:
    "Number of distinct sessions recorded for this user (from session ids on the profile).",
  firstReferrer:
    "Referrer URL or category from the user’s earliest recorded landing.",
  lastReferrer:
    "Referrer from the latest recorded landing or navigation for this user.",
  firstCity:
    "City inferred or reported for the user’s first known location signal.",
  firstCountry:
    "Country inferred or reported for the user’s first known location signal.",
  lastCity:
    "City from the user’s most recent location signal.",
  lastCountry:
    "Country from the user’s most recent location signal.",
  deviceType:
    "Primary device class for this user (e.g. desktop, mobile, tablet), derived from observed user agents.",
  browser:
    "Browser name most recently or most often associated with this user.",
  operatingSystem:
    "Operating system family associated with this user profile (e.g. Windows, macOS, iOS, Android).",
  walletType:
    "Category or type of wallet linked on the user profile when available.",
  walletChain:
    "Blockchain network associated with the user’s linked wallet when available.",
  hasCustomProperties:
    "Whether the user profile has any custom properties set (non-empty JSON object).",
};

/** Built-in group row fields (cohort group filters / group list). */
const GROUP_FIXED_DESCRIPTIONS = {
  group_key:
    "The group type namespace (e.g. company, team). Users match if they belong to any group whose key equals this value.",
  group_id:
    "The stable id for a group within its group key (e.g. company id). Optionally scoped by group type when you pick a property under a specific key.",
  member_count:
    "Number of user identities currently linked to this group. Filter users who are in a group whose member count meets your condition.",
  last_updated_at:
    "When the group profile or membership was last updated in Trodo.",
};

/** Session list filters — field names match getSessionProperties. */
const SESSION_PROPERTY_DESCRIPTIONS = {
  country: "Country associated with this session (typically from IP or client context).",
  city: "City associated with this session when available.",
  pages: "Pages or URLs visited during this session (path-level signal for navigation).",
  entryPage: "First page URL or path where the session began.",
  exitPage: "Last page URL or path recorded before the session ended.",
  browser: "Browser family or name observed for this session.",
  operatingSystem: "Operating system inferred from the user agent for this session.",
  deviceType: "Coarse device class for this session (desktop, mobile, tablet, etc.).",
  referrer: "Referrer URL or category for the landing that started or continued this session.",
  isWeb3User: "Whether this session was tagged as Web3-related.",
  walletConnected: "Whether a wallet connection was active during this session.",
  isBounce: "Whether this session met your product’s bounce definition (e.g. very short or single-page).",
  startTime: "When the session started: use for time-based session filters.",
  pagesViewed: "Count of page views recorded in this session.",
  duration: "Length of the session in seconds (engaged or total, per product rules).",
  visitsByUser: "How many visits this user had up to or including this session (rolling count).",
};

const FIRST_TIME_FILTER_DESCRIPTION =
  "Restricts results to events that occur in a user’s first session within the selected time range. Useful for activation, onboarding, and first-touch analysis.";

const COHORT_FILTER_DESCRIPTION =
  "Limits results to users who are members of the selected cohort. Cohort membership is evaluated for the team’s saved cohort definition.";

/**
 * @param {object} prop - { name, label, source, isCustom, type?, groupKey?, isBuiltin? }
 * @returns {{ title: string, body: string } | { title: string, titleOnly: true }}
 */
export function getPropertyDescriptionPreview(prop) {
  if (!prop) return null;
  const title = prop.label || prop.name || "Property";

  if (prop.source === "group") {
    const fixedBody = GROUP_FIXED_DESCRIPTIONS[prop.name];
    if (fixedBody) return { title, body: fixedBody };
    const body = prop.groupKey
      ? `Custom property on “${prop.groupKey}” group profiles. Users match if they belong to a group of this type where this field satisfies your filter.`
      : "Custom field on group profiles. Users match if they belong to a group row where this property satisfies your condition.";
    return { title, body };
  }

  if (prop.source === "event") {
    if (prop.type === "event_count" || (typeof prop.name === "string" && prop.name.startsWith("event_count:"))) {
      const eventName = typeof prop.name === "string" ? prop.name.replace(/^event_count:/, "") : "";
      const label = eventName || prop.label || "this event";
      const body =
        `WHO-style filter: segment users by how many times they performed “${label}” within the time range you set on the filter chip. Use Did / Did not and numeric operators to compare event counts.`;
      return { title, body };
    }
    const body = EVENT_PROPERTY_DESCRIPTIONS[prop.name];
    if (body) return { title, body };
    return { title, titleOnly: true };
  }

  if (prop.source === "user") {
    if (prop.isCustom || (typeof prop.name === "string" && prop.name.startsWith("custom."))) {
      const body =
        "Custom property on the user profile. Values are set by your application (identify, people, or SDK profile APIs).";
      return { title, body };
    }
    const body =
      USER_PROPERTY_DESCRIPTIONS[prop.name] || USER_IDENTITY_FIXED_DESCRIPTIONS[prop.name];
    if (body) return { title, body };
    return { title, titleOnly: true };
  }

  if (prop.source === "session") {
    const body = SESSION_PROPERTY_DESCRIPTIONS[prop.name];
    if (body) return { title, body };
    return { title, titleOnly: true };
  }

  return { title, titleOnly: true };
}

export function getFirstTimeFilterDescription() {
  return FIRST_TIME_FILTER_DESCRIPTION;
}

export function getCohortFilterDescription() {
  return COHORT_FILTER_DESCRIPTION;
}
